import { ShieldCheck, FileText, MapPin } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/motion";
import { ORG, MISSION, TEAM, MANIFESTO, IMAGES } from "../data/site";

const OBJECTIVES = [
  "Rehabilitate individuals overcoming addiction with dignity and long-term care.",
  "Nurture young children and mothers through the Balwadi centre and nutrition support.",
  "Support HIV/AIDS-affected families with confidential, compassionate counselling.",
  "Provide a home for orphaned children and elderly persons without family.",
  "Equip women and youth with skills and livelihoods for self-reliance.",
  "Improve public health, sanitation and safe drinking water access.",
  "Protect the environment and raise awareness across the community.",
];

export default function About() {
  return (
    <>
      <PageHeader
        eyebrow="About Us"
        title="Our Story"
        subtitle="Founded in 2005 in Bishnupur District, Manipur — a community rising together."
        image={IMAGES.children}
      />

      {/* Story + mission */}
      <section className="bg-brand-cream">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 grid gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="overflow-hidden rounded-3xl">
              <img src={IMAGES.women} alt="Community members of Thanga, Bishnupur" className="aspect-[3/4] w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <span className="text-xs uppercase tracking-[0.24em] text-brand-primary">Our Mission</span>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-brand-ink leading-tight">
              Restoring dignity, health and hope — one family at a time
            </h2>
            <p className="mt-6 text-brand-muted leading-relaxed text-lg">{MISSION}</p>
            <p className="mt-5 text-brand-muted leading-relaxed">
              From our home in Ithing Awang Leikai, Thanga Part-II, our local social workers have built trusted, grassroots programs that meet people where they are — beside the Loktak wetlands and across Bishnupur District.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Objectives */}
      <section className="bg-brand-surface">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
          <Reveal className="max-w-2xl mb-12">
            <span className="text-xs uppercase tracking-[0.24em] text-brand-primary">Our Objectives</span>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-brand-ink">What we set out to do</h2>
          </Reveal>
          <div className="grid gap-5 md:grid-cols-2">
            {OBJECTIVES.map((o, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="flex gap-4 rounded-2xl border border-brand-line bg-brand-cream p-6">
                  <span className="font-serif italic text-3xl text-brand-primary/30 leading-none">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-brand-muted leading-relaxed">{o}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section id="team" className="scroll-mt-24 bg-brand-cream">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
          <Reveal className="max-w-2xl mb-14">
            <span className="text-xs uppercase tracking-[0.24em] text-brand-primary">Our Team</span>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-brand-ink">The Managing Committee</h2>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {TEAM.map((m, i) => (
              <Reveal key={m.name} delay={i * 0.06}>
                <div data-testid={`team-member-${i}`} className="group rounded-3xl border border-brand-line bg-brand-surface p-7 h-full">
                  <div className="overflow-hidden rounded-2xl aspect-[4/5] bg-brand-primary/10">
                    <img
                      src={m.photo}
                      alt={`${m.name}, ${m.role}`}
                      loading="lazy"
                      className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <h3 className="mt-6 font-serif text-xl text-brand-ink">{m.name}</h3>
                  <p className="mt-1 text-sm font-semibold text-brand-primary">{m.role}</p>
                  <p className="mt-3 text-sm text-brand-muted">{m.occupation}</p>
                  <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-brand-muted"><MapPin className="h-3.5 w-3.5" /> {m.location}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Registration & legal */}
      <section id="transparency" className="scroll-mt-24 bg-brand-teal text-brand-cream">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 grid gap-12 lg:grid-cols-2">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.24em] text-brand-accent">Registration & Legal</span>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl">Trust, earned and documented</h2>
            <p className="mt-6 text-brand-cream/75 leading-relaxed">
              Equipping New Life is a registered charitable society operating with full statutory recognition in Manipur. Certificates are available on request.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="space-y-5">
            <div className="flex gap-4 rounded-2xl border border-brand-cream/15 bg-brand-cream/5 p-6">
              <ShieldCheck className="h-7 w-7 shrink-0 text-brand-accent" />
              <div>
                <p className="font-semibold">Registered Society</p>
                <p className="mt-1 text-sm text-brand-cream/75">{ORG.regAct} — {ORG.regNo}, dated {ORG.regDate}, issued by the {ORG.regAuthority}.</p>
              </div>
            </div>
            <div className="flex gap-4 rounded-2xl border border-brand-cream/15 bg-brand-cream/5 p-6">
              <FileText className="h-7 w-7 shrink-0 text-brand-accent" />
              <div>
                <p className="font-semibold">Government Certification</p>
                <p className="mt-1 text-sm text-brand-cream/75">{ORG.certification}</p>
              </div>
            </div>
            <div className="flex gap-4 rounded-2xl border border-brand-cream/15 bg-brand-cream/5 p-6">
              <FileText className="h-7 w-7 shrink-0 text-brand-accent" />
              <div>
                <p className="font-semibold">Transparency & Annual Reports</p>
                <p className="mt-1 text-sm text-brand-cream/75">Financial disclosures and annual reports will be published here. {/* <-- ADD REPORTS WHEN AVAILABLE --> */}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
